import * as Sentry from '@sentry/vue'
import { type Transport } from '@sentry/types'
import type { App } from 'vue'
import type { Router } from 'vue-router'
import { getEnv } from './env'
import { ApiError as MobileAuthApiError } from '@/generated/mobileAuthApi'
import { ApiError } from '@/generated/api'
import { AxiosError } from 'axios'
import log from 'loglevel'
import { FRONTEND_ID } from './frontendId'

type TransportFactory = (() => Transport) | undefined

export interface SentryConfig {
  dsn: string
  environment: string
  sampleRate: number | undefined
  replaysSessionSampleRate: number | undefined
}

export function configFromEnv(): SentryConfig {
  return {
    dsn: getEnv('sentryDsn'),
    environment: getEnv('env'),
    sampleRate: getEnv('sentrySampleRate'),
    replaysSessionSampleRate: getEnv('sentryReplaySessionSampleRate')
  }
}

const apiErrorEventProcessor = (event: Sentry.Event, hint: Sentry.EventHint) => {
  const error = hint.originalException
  if (!(error instanceof ApiError || error instanceof MobileAuthApiError)) {
    return event
  }

  event.message = `ApiError - ${error.request.method ?? 'Unknown method'} ${error.request.url ?? 'Unknown url"'}`

  event.contexts = event.contexts || {}
  event.contexts['request'] = {
    method: error.request.method,
    url: error.request.url,
    body: error.request.body
  }

  event.contexts['response'] = {
    body: error.body,
    statusCode: error.status,
    message: error.message
  }

  return event
}

const axiosErrorEventProcessor = (event: Sentry.Event, hint: Sentry.EventHint) => {
  const error = hint.originalException
  if (!(error instanceof AxiosError)) {
    return event
  }

  event.message = `AxiosError - ${error.config?.method} ${error.config?.url}`

  event.contexts = event.contexts || {}
  event.contexts['request'] = {
    method: error?.config?.method,
    url: error?.config?.url,
    data: error?.config?.data
  }

  event.contexts['response'] = {
    body: error.response?.data,
    statusCode: error.response?.status
  }

  return event
}

export function initSentry(
  config: SentryConfig,
  app: App,
  router: Router,
  transportFactory?: TransportFactory
) {
  if (config.dsn === '') {
    log.debug('Not enabling sentry due to empty dsn.')
    return
  }

  Sentry.init({
    transport: transportFactory,
    app,
    dsn: config.dsn,
    environment: config.environment,
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router)
      })
    ],

    sampleRate: config.sampleRate,

    // Session Replay
    replaysSessionSampleRate: config.replaysSessionSampleRate,
    replaysOnErrorSampleRate: 1.0,

    ignoreErrors: [/UserClientUnsupportedError/, /CountryUnsupportedError/],

    maxValueLength: 2048
  })

  Sentry.addEventProcessor(apiErrorEventProcessor)
  Sentry.addEventProcessor(axiosErrorEventProcessor)

  Sentry.setContext('frontend', { frontendId: FRONTEND_ID })
}
